// The browser side of a shared window: join with a throwaway key, reach the
// computer through a relay, authenticate, and open its terminal stream.
// Mirrors go/internal/{authz,term,wire}; the computer enforces what a guest
// may do, this page only shows it.

import { create, fromBinary, toBinary, type DescMessage, type MessageShape } from "@bufbuild/protobuf";
import { noise } from "@chainsafe/libp2p-noise";
import { yamux } from "@chainsafe/libp2p-yamux";
import { circuitRelayTransport } from "@libp2p/circuit-relay-v2";
import { generateKeyPair, publicKeyFromRaw } from "@libp2p/crypto/keys";
import { identify } from "@libp2p/identify";
import type { Connection, Libp2p, PrivateKey, Stream } from "@libp2p/interface";
import { peerIdFromPrivateKey } from "@libp2p/peer-id";
import { webSockets } from "@libp2p/websockets";
import {
  AuthHelloSchema,
  AuthResultSchema,
  CAKeySetSchema,
  DeviceCertSchema,
  DeviceRole,
  ForwardOpenSchema,
  ForwardOpenedSchema,
  SignedSchema,
  CtlMessageSchema,
  CtlRequestSchema,
  TermClientSchema,
  TermHostSchema,
  type CtlRequest,
  type CtlResponse,
  type DeviceCert,
  type TermHost,
} from "@luwu/proto";
import { multiaddr } from "@multiformats/multiaddr";
import { createLibp2p } from "libp2p";

import { LUWU_API, browserRelays } from "./server";

/** Luwu's control plane; share links (/s/<code>) are Luwu's. Signed-in pages use apiBase(). */
export const API = LUWU_API;
const AUTH = "/luwu/auth/1.0.0";
const TERM = "/luwu/term/1.0.0";
const CTL = "/luwu/ctl/1.0.0";
const FORWARD = "/luwu/forward/1.0.0";
const PROTO_REV = 1;
const VERSION = "web/0.1.0";


const utf8 = (s: string) => new TextEncoder().encode(s);
const b64 = (u: Uint8Array) => btoa(String.fromCharCode(...u));
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const concat = (a: Uint8Array, b: Uint8Array) => {
  const out = new Uint8Array(a.length + b.length);
  out.set(a);
  out.set(b, a.length);
  return out;
};

export interface JoinInfo {
  cert: string;
  ca_keys: string;
  host: { device_id: string; peer_id: string; name: string; relays: string[] };
  share: { id: string; kind?: "window" | "screen"; window_label: string; can_type: boolean; owner_name: string; expires_at: number };
}

export class JoinError extends Error {
  constructor(
    message: string,
    readonly status = 0,
  ) {
    super(message);
  }
}

/** "SCBH-57S3-JZP0", "scbh57s3jzp0" or a whole link → the code as typed by the owner's app. */
export function codeFrom(s: string): string {
  const m = s.match(/\/s\/([^/?#]+)/);
  return (m ? m[1]! : s).trim();
}

/** Joins a share with a fresh key; the API needs no account. */
export async function join(code: string, name: string): Promise<{ key: PrivateKey; info: JoinInfo }> {
  const key = await generateKeyPair("Ed25519");
  const peerId = peerIdFromPrivateKey(key).toString();
  const ts = Math.floor(Date.now() / 1000);
  const sig = await key.sign(utf8(`luwu-join-v1\n${code}\n${peerId}\n${ts}`));
  const r = await fetch(`${API}/v1/shares/join`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code, peer_id: peerId, name, platform: "web", via: "web", ts, sig: b64(sig) }),
  });
  const body = (await r.json().catch(() => ({}))) as JoinInfo & { error?: string };
  if (!r.ok) throw new JoinError(body.error || `The server answered ${r.status}`, r.status);
  return { key, info: body };
}

// Varint-delimited protobuf frames (Go: protodelim), at most 1 MiB.
const MAX_FRAME = 1 << 20;

function varint(n: number): Uint8Array {
  const out: number[] = [];
  while (n >= 0x80) {
    out.push((n & 0x7f) | 0x80);
    n >>>= 7;
  }
  out.push(n);
  return Uint8Array.from(out);
}

/** Reads delimited messages from a stream. */
class FrameReader {
  private buf: Uint8Array = new Uint8Array(0);
  private it: AsyncIterator<Uint8Array | { subarray(): Uint8Array }>;
  constructor(stream: Stream) {
    this.it = stream[Symbol.asyncIterator]() as AsyncIterator<Uint8Array | { subarray(): Uint8Array }>;
  }
  async read<D extends DescMessage>(schema: D): Promise<MessageShape<D> | null> {
    for (;;) {
      let len = 0;
      let shift = 0;
      let i = 0;
      let complete = false;
      for (; i < this.buf.length && i < 5; i++) {
        const b = this.buf[i]!;
        len |= (b & 0x7f) << shift;
        shift += 7;
        if (b < 0x80) {
          complete = true;
          i++;
          break;
        }
      }
      if (complete) {
        if (len > MAX_FRAME) throw new Error("frame too large");
        if (this.buf.length >= i + len) {
          const body = this.buf.subarray(i, i + len);
          this.buf = this.buf.subarray(i + len);
          return fromBinary(schema, body);
        }
      }
      const chunk = await this.chunk();
      if (!chunk) return null;
      this.buf = this.buf.length ? concat(this.buf, chunk) : chunk;
    }
  }
  /** The next raw chunk from the stream, or null at its end. */
  async chunk(): Promise<Uint8Array | null> {
    const next = await this.it.next();
    if (next.done) return null;
    return next.value instanceof Uint8Array ? next.value : next.value.subarray();
  }
  /** Bytes read past the last frame, which belong to what follows. */
  rest(): Uint8Array {
    const r = this.buf;
    this.buf = new Uint8Array(0);
    return r;
  }
}

function send<D extends DescMessage>(stream: Stream, schema: D, msg: MessageShape<D>) {
  const body = toBinary(schema, msg);
  stream.send(concat(varint(body.length), body));
}

/** Checks a Signed DeviceCert against the CA key set the join returned. */
async function verifyCert(signedBytes: Uint8Array | undefined, caKeys: Uint8Array): Promise<DeviceCert> {
  const caSigned = fromBinary(SignedSchema, caKeys);
  const set = fromBinary(CAKeySetSchema, caSigned.body);
  const keyFor = (kid: string) => set.keys.find((k) => k.kid === kid);
  const verify = async (domain: string, s: { body: Uint8Array; kid: string; sig: Uint8Array }) => {
    const k = keyFor(s.kid);
    if (!k) throw new Error("signed by an unknown CA key");
    if (!(await publicKeyFromRaw(k.ed25519Pub).verify(concat(utf8(domain), s.body), s.sig))) throw new Error("bad CA signature");
  };
  await verify("luwu-ca-v1\0", caSigned);
  if (!signedBytes) throw new Error("the computer sent no certificate");
  const signed = fromBinary(SignedSchema, signedBytes);
  await verify("luwu-cert-v1\0", signed);
  const cert = fromBinary(DeviceCertSchema, signed.body);
  if (Number(cert.expiresAt) < Date.now() / 1000) throw new Error("the computer's certificate has expired");
  return cert;
}

export interface Session {
  node: Libp2p;
  stream: Stream;
  path: string;
  /** Sends keystrokes; the computer drops them for view-only shares. */
  input(data: Uint8Array): void;
  resize(cols: number, rows: number): void;
  history(lines: number): void;
  /** Owner devices: size the window to this client (true) or just watch its size. */
  fit(on: boolean): void;
  close(): Promise<void>;
}

export type Status = "relay" | "auth" | "terminal";

/**
 * Connects to the shared computer and opens the terminal. Messages from the
 * computer go to onMessage; the promise resolves once the terminal is open.
 */
export async function open(
  key: PrivateKey,
  info: JoinInfo,
  size: { cols: number; rows: number },
  onMessage: (m: TermHost) => void,
  onStatus: (s: Status) => void,
): Promise<Session> {
  const { node, conn } = await connect(key, info, onStatus);
  onStatus("terminal");
  return openTerm(node, conn, { cols: size.cols, rows: size.rows, readOnly: !info.share.can_type }, onMessage);
}

/**
 * Opens a terminal stream on a connected computer: a guest's shared window,
 * or (for the account's own devices) any session and window.
 */
export async function openTerm(
  node: Libp2p,
  conn: Connection,
  o: { sessionId?: string; windowId?: string; cols: number; rows: number; readOnly?: boolean },
  onMessage: (m: TermHost) => void,
  stopNode = true,
): Promise<Session> {
  const stream = await conn.newStream(TERM, { runOnLimitedConnection: true, signal: AbortSignal.timeout(15_000) });
  stream.inactivityTimeout = 0;
  const reader = new FrameReader(stream);
  send(stream, TermClientSchema, create(TermClientSchema, { m: { case: "open", value: { sessionId: o.sessionId ?? "", windowId: o.windowId ?? "", cols: o.cols, rows: o.rows, readOnly: !!o.readOnly } } }));
  const first = await reader.read(TermHostSchema);
  if (!first) throw new Error("the terminal closed before it opened");
  if (first.m.case === "exit") throw new Error(first.m.value.reason || "the terminal closed");
  onMessage(first);
  void (async () => {
    try {
      for (;;) {
        const m = await reader.read(TermHostSchema);
        if (!m) break;
        onMessage(m);
      }
    } catch {
      // the stream ended
    }
    onMessage(create(TermHostSchema, { m: { case: "exit", value: { reason: "connection lost" } } }));
  })();

  return {
    node,
    stream,
    path: pathOf(conn),
    input: (data) => send(stream, TermClientSchema, create(TermClientSchema, { m: { case: "input", value: { data } } })),
    resize: (cols, rows) => send(stream, TermClientSchema, create(TermClientSchema, { m: { case: "resize", value: { cols, rows } } })),
    history: (lines) => send(stream, TermClientSchema, create(TermClientSchema, { m: { case: "history", value: { lines } } })),
    fit: (on) => send(stream, TermClientSchema, create(TermClientSchema, { m: { case: "fit", value: { on } } })),
    close: async () => {
      try {
        send(stream, TermClientSchema, create(TermClientSchema, { m: { case: "close", value: {} } }));
        await stream.close();
      } catch {
        // already gone
      }
      if (stopNode) await node.stop();
    },
  };
}

let ctlId = 0n;
/** One control request (/luwu/ctl/1.0.0) on its own stream; the computer's answer or its error. */
export async function ctlCall(conn: Connection, req: CtlRequest["req"]): Promise<CtlResponse> {
  const stream = await conn.newStream(CTL, { runOnLimitedConnection: true, signal: AbortSignal.timeout(15_000) });
  try {
    const id = ++ctlId;
    send(stream, CtlMessageSchema, create(CtlMessageSchema, { msg: { case: "request", value: create(CtlRequestSchema, { id, req }) } }));
    const reader = new FrameReader(stream);
    for (;;) {
      const m = await reader.read(CtlMessageSchema);
      if (!m) throw new Error("the computer closed the connection");
      if (m.msg.case === "response" && m.msg.value.id === id) {
        if (m.msg.value.error?.message) throw new Error(m.msg.value.error.message);
        return m.msg.value;
      }
    }
  } finally {
    await stream.close().catch(() => {});
  }
}

const pathOf = (conn: Connection) => (conn.remoteAddr.toString().includes("/p2p-circuit") ? "relay" : "direct");

/** What reaching a computer takes: our cert (a guest's or a device's), the CA keys, and the computer. */
export interface Creds {
  cert: string; // base64 Signed DeviceCert
  ca_keys: string; // base64 Signed CAKeySet
  host: { peer_id: string; name: string };
}

const connect = (key: PrivateKey, info: Creds, onStatus: (s: Status) => void) => connectHost(key, info, onStatus);

/** Reaches a computer through a relay and does the auth handshake. */
export async function connectHost(key: PrivateKey, info: Creds, onStatus: (s: Status) => void): Promise<{ node: Libp2p; conn: Connection }> {
  const node = await createLibp2p({
    privateKey: key,
    transports: [webSockets(), circuitRelayTransport()],
    connectionEncrypters: [noise()],
    streamMuxers: [yamux()],
    connectionGater: { denyDialMultiaddr: () => false },
    services: { identify: identify() },
  });
  const target = info.host.peer_id;
  const addrs = browserRelays().map((r) => multiaddr(`${r}/p2p-circuit/p2p/${target}`));
  onStatus("relay");
  let conn;
  let lastErr: unknown;
  for (const a of addrs) {
    try {
      conn = await node.dial(a, { signal: AbortSignal.timeout(20_000) });
      break;
    } catch (err) {
      lastErr = err;
    }
  }
  if (!conn) {
    await node.stop();
    throw new Error(`Couldn't reach ${info.host.name || "the computer"}. Is it online? (${(lastErr as Error)?.message ?? "no route"})`);
  }

  // The auth handshake: our guest cert for theirs, verified against the CA.
  onStatus("auth");
  const auth = await conn.newStream(AUTH, { runOnLimitedConnection: true, signal: AbortSignal.timeout(15_000) });
  send(auth, AuthHelloSchema, create(AuthHelloSchema, { cert: fromBinary(SignedSchema, unb64(info.cert)), caps: [], appVersion: VERSION, protoRev: PROTO_REV }));
  const res = await new FrameReader(auth).read(AuthResultSchema);
  await auth.close().catch(() => {});
  if (!res) throw new Error("the computer closed the connection");
  if (!res.ok) {
    if (res.pendingLocalApproval) throw new Error("This browser isn't allowed on that computer yet. Allow it from your phone (the computer's Devices list), or run `luwu devices approve` on the computer, then try again.");
    throw new Error(res.reason === "the share has ended" || res.reason === "removed from the share" ? `The ${res.reason}.` : `Not allowed in: ${res.reason}`);
  }
  const hostCert = await verifyCert(res.hello?.cert ? toBinary(SignedSchema, res.hello.cert) : undefined, unb64(info.ca_keys));
  const mine = fromBinary(DeviceCertSchema, fromBinary(SignedSchema, unb64(info.cert)).body);
  // Ours, a teammate's computer shared with the team we're in, or for a
  // guest the computer the share names (on a shared machine the person who
  // shared isn't the computer's account).
  const sameTeam = mine.teamId !== "" && hostCert.teamId === mine.teamId;
  const granted = mine.role === DeviceRole.GUEST && !!mine.grant && hostCert.deviceId === mine.grant.hostDeviceId;
  if (hostCert.peerId !== target || hostCert.role !== DeviceRole.HOST || (hostCert.accountId !== mine.accountId && !sameTeam && !granted)) {
    await node.stop();
    throw new Error("The computer's identity didn't check out.");
  }

  return { node, conn };
}

/**
 * A WebSocket-like channel over a forward stream to the computer's screen
 * (port 5900), for noVNC's RFB, which takes any object with these members.
 */
export interface ScreenChannel {
  binaryType: string;
  protocol: string;
  readyState: string;
  onopen: ((e: Event) => void) | null;
  onmessage: ((e: { data: ArrayBuffer }) => void) | null;
  onclose: ((e: { code: number; reason: string; wasClean: boolean }) => void) | null;
  onerror: ((e: Event) => void) | null;
  send(data: ArrayBuffer | Uint8Array): void;
  close(): void;
}

export interface ScreenSession {
  channel: ScreenChannel;
  path: string;
  close(): Promise<void>;
}

/** Connects to the shared computer and opens a stream to its screen. */
export async function openScreen(key: PrivateKey, info: JoinInfo, onStatus: (s: Status | "screen") => void): Promise<ScreenSession> {
  const { node, conn } = await connect(key, info, onStatus);
  onStatus("screen");
  const stream = await conn.newStream(FORWARD, { runOnLimitedConnection: true, signal: AbortSignal.timeout(15_000) });
  stream.inactivityTimeout = 0;
  const reader = new FrameReader(stream);
  send(stream, ForwardOpenSchema, create(ForwardOpenSchema, { port: 5900 }));
  const opened = await reader.read(ForwardOpenedSchema);
  if (!opened) throw new Error("the computer closed the connection");
  if (!opened.ok) throw new Error(opened.error?.message || "the computer refused the screen");

  const toBuf = (u: Uint8Array) => u.buffer.slice(u.byteOffset, u.byteOffset + u.byteLength) as ArrayBuffer;
  let closed = false;
  // Bytes that arrive before RFB sets onmessage wait here.
  const queue: ArrayBuffer[] = [];
  let handler: ((e: { data: ArrayBuffer }) => void) | null = null;
  const deliver = (data: ArrayBuffer) => (handler ? handler({ data }) : queue.push(data));
  const ch: ScreenChannel = {
    binaryType: "arraybuffer",
    protocol: "",
    readyState: "open",
    onopen: null,
    get onmessage() {
      return handler;
    },
    set onmessage(f) {
      handler = f;
      while (handler && queue.length) handler({ data: queue.shift()! });
    },
    onclose: null,
    onerror: null,
    send: (data) => {
      if (closed) return;
      stream.send(data instanceof Uint8Array ? data : new Uint8Array(data));
    },
    close: () => {
      if (closed) return;
      closed = true;
      ch.readyState = "closed";
      void stream.close().catch(() => stream.abort(new Error("closed")));
    },
  };
  // Bytes from the computer, starting with any read along with the reply.
  void (async () => {
    const first = reader.rest();
    if (first.length) deliver(toBuf(first));
    try {
      for (;;) {
        const chunk = await reader.chunk();
        if (!chunk) break;
        deliver(toBuf(chunk));
      }
    } catch {
      // the stream ended
    }
    const clean = closed;
    closed = true;
    ch.readyState = "closed";
    ch.onclose?.({ code: clean ? 1000 : 1006, reason: "", wasClean: clean });
  })();
  return {
    channel: ch,
    path: pathOf(conn),
    close: async () => {
      ch.close();
      await node.stop();
    },
  };
}
